import { COMMON } from '@brickup/shared/locales/de/common'
import { applySortOrder, type SortedList } from '@brickup/shared/utils'
import { apiClient } from './client'
import type { QueryKeyList } from './query-keys'
import {
  useMutationWithInvalidation,
  type MutationCallbacks,
  type OptimisticWrite,
} from '@/hooks/use-mutation-with-invalidation'

/**
 * The answer a write that refuses carries back, as the query it travels as.
 *
 * Some writes report what they would do rather than doing it, and go ahead
 * only when called again with what they reported — see `useRefusal`. The
 * answer is opaque here on purpose: the factory's job is to put it on the
 * request, not to know which refusals exist. Axios drops an undefined key, so
 * a caller that has nothing to answer sends nothing.
 */
export type WriteAnswer = Record<string, string | number | undefined>

/** What a patch takes: the row, the fields, and whatever answer a refusing
 *  write is waiting for. */
type PatchVars<TPatch> = { id: number; dto: TPatch; answer?: WriteAnswer }

// `usePatch` takes it here; the hand-written hooks beside the factory take the
// same `answer` so one convention reaches every confirming write. A delete is
// the exception — widening its variables to a union breaks the
// `UseMutationResult<…, number>` that `use-delete-catalogue-entry` is built
// on, so `useDeleteGroup` is written out.

/**
 * Create / patch / delete against one REST endpoint.
 *
 * The three differ only in the path and which keys go stale, so they are built
 * rather than written out — every admin-kept list needs the same three, and a
 * hand-written one is a hook that forgets a key.
 *
 * Options are taken per hook rather than at `mutate()`: a handler passed there
 * runs on top of the default snackbar, so a pane that wants to put the message
 * in a field would raise both.
 */
export function makeCrudHooks<TCreate, TPatch, TCreated = unknown>(
  basePath: string,
  invalidates: QueryKeyList,
) {
  return {
    useCreate: (options?: MutationCallbacks<TCreated, TCreate>) =>
      useMutationWithInvalidation(
        async (dto: TCreate) => {
          const { data } = await apiClient.post<TCreated>(basePath, dto)
          return data
        },
        invalidates,
        options,
      ),

    usePatch: (options?: MutationCallbacks<unknown, PatchVars<TPatch>>) =>
      useMutationWithInvalidation(
        async ({ id, dto, answer }: PatchVars<TPatch>) => {
          const { data } = await apiClient.patch(`${basePath}/${id}`, dto, {
            params: answer,
          })
          return data
        },
        invalidates,
        options,
      ),

    useDelete: (options?: MutationCallbacks<unknown, number>) =>
      useMutationWithInvalidation(
        (id: number) => apiClient.delete(`${basePath}/${id}`),
        invalidates,
        { errorFallback: COMMON.errors.deleteFailed, ...options },
      ),
  }
}

/**
 * The whole-list reorder that goes with them. The payload is always the full
 *  order, so there is nothing per-entity to parameterise.
 *
 * Optimistic by construction: a dropped row is drawn where it was dropped and
 * goes back only if the server refuses. `applySortOrder` finds the list inside
 * whatever the key caches, so no endpoint has to describe its own response
 * shape to get that.
 */
export function makeSortHook(
  path: string,
  invalidates: QueryKeyList,
  /** The list this endpoint puts in order, and the caches that hold it. */
  list: SortedList & { keys?: QueryKeyList },
) {
  return () =>
    useMutationWithInvalidation(
      async (items: Array<{ id: number; sortOrder: number }>) => {
        await apiClient.patch(path, { items })
      },
      invalidates,
      { optimistic: sortOptimistically(list.keys ?? invalidates, list) },
    )
}

/**
 * What a sort mutation hands `useMutationWithInvalidation` to draw its own
 * result.
 *
 * `keys` is usually narrower than what the write invalidates: the list lives
 * in one cache, and walking the others is a walk that can only come back
 * empty.
 */
export function sortOptimistically(
  keys: QueryKeyList,
  list: SortedList,
): OptimisticWrite<readonly Record<string, unknown>[]> {
  return {
    keys,
    apply: (cached, items) => applySortOrder(cached, items, list),
  }
}
